import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Button, Checkbox } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import RTGSActionDialog from './RTGSActionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { useToast } from './ui/ToastProvider';
import { transactionStore } from '../utils/transactionStore';
type RTGSStatus = 'SENT_TO_BANK' | 'REVERSED' | 'MANUALLY_SETTLED';
interface RTGSTransaction {
  id: string;
  mtoName: string;
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  rtgsStatus: RTGSStatus;
  sentToRTGSAt: string;
  createdAt: string;
  currentStage: string;
  lifecycle: string;
  transactionStatus: string;
  status: string;
}
const RTGSTransactionsTable: React.FC = () => {
  const [transactions, setTransactions] = useState<RTGSTransaction[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [selectedTransaction, setSelectedTransaction] = useState<RTGSTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rtgsDialogOpen, setRtgsDialogOpen] = useState(false);
  const [rtgsAction, setRtgsAction] = useState<'reverse' | 'settle' | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<RTGSTransaction | null>(null);
  const {
    showToast
  } = useToast();
  // Subscribe to transaction store updates
  useEffect(() => {
    const updateTransactions = () => {
      setTransactions(transactionStore.getRTGSTransactions());
    };
    // Initial load
    updateTransactions();
    // Subscribe to changes
    const unsubscribe = transactionStore.subscribe(updateTransactions);
    return unsubscribe;
  }, []);
  const handleSelectTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const sentToBankIds = transactions.filter(t => t.rtgsStatus === 'SENT_TO_BANK').map(t => t.id);
      setSelectedTransactions(new Set(sentToBankIds));
    } else {
      setSelectedTransactions(new Set());
    }
  };
  const handleBulkAction = (action: 'reverse' | 'settle') => {
    if (selectedTransactions.size === 0) {
      showToast('Please select at least one transaction', 'warning');
      return;
    }
    setRtgsAction(action);
    setRtgsDialogOpen(true);
  };
  const handleRTGSConfirm = (notes?: string) => {
    if (!rtgsAction) return;
    const statusMap = {
      reverse: 'REVERSED' as RTGSStatus,
      settle: 'MANUALLY_SETTLED' as RTGSStatus
    };
    const newStatus = statusMap[rtgsAction];
    // Update in store - pass the comment so it's tracked
    transactionStore.updateRTGSStatus(Array.from(selectedTransactions), newStatus, notes);
    showToast(`${selectedTransactions.size} transaction(s) marked as ${newStatus.replace(/_/g, ' ')}`, 'success');
    setRtgsDialogOpen(false);
    setSelectedTransactions(new Set());
    setRtgsAction(null);
  };
  const handleCancelClick = (transaction: RTGSTransaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedCancelTransaction) return;
    transactionStore.updateRTGSStatus([selectedCancelTransaction.id], 'REVERSED' as RTGSStatus, `Cancelled: ${reason}`);
    showToast(`Cancellation request submitted for ${selectedCancelTransaction.id}`, 'success');
    setCancelDialogOpen(false);
    setSelectedCancelTransaction(null);
  };
  const handleBulkCancelClick = () => {
    if (selectedTransactions.size === 0) return;
    setBulkCancelDialogOpen(true);
  };
  const handleBulkCancelConfirm = async (reason: string) => {
    const count = selectedTransactions.size;
    transactionStore.updateRTGSStatus(Array.from(selectedTransactions), 'REVERSED' as RTGSStatus, `Bulk cancelled: ${reason}`);
    setSelectedTransactions(new Set());
    showToast(`Cancellation request submitted for ${count} transactions`, 'success');
  };
  const rowActions: RowAction<RTGSTransaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => handleCancelClick(row),
    condition: row => row.rtgsStatus === 'SENT_TO_BANK'
  }];
  const columns: Column<RTGSTransaction>[] = [{
    id: 'select',
    label: <Checkbox checked={selectedTransactions.size > 0 && selectedTransactions.size === transactions.filter(t => t.rtgsStatus === 'SENT_TO_BANK').length} indeterminate={selectedTransactions.size > 0 && selectedTransactions.size < transactions.filter(t => t.rtgsStatus === 'SENT_TO_BANK').length} onChange={e => handleSelectAll(e.target.checked)} sx={{
      color: '#8B5CF6',
      '&.Mui-checked': {
        color: '#8B5CF6'
      }
    }} data-id="element-2203" data-mp="1jhc3gucl0t8w" />,
    renderCell: row => <Checkbox checked={selectedTransactions.has(row.id)} onChange={() => handleSelectTransaction(row.id)} disabled={row.rtgsStatus !== 'SENT_TO_BANK'} sx={{
      color: '#8B5CF6',
      '&.Mui-checked': {
        color: '#8B5CF6'
      }
    }} data-id="element-2204" data-mp="7zt6vktibojb" />
  }, {
    id: 'id',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-2205" data-mp="1o81gcma0of8z">
          {row.id}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-2206" data-mp="1y4o3xitq9kep">
          {row.mtoName}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2207" data-mp="39n8mvsz9qy1b" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-2208" data-mp="o7pm9jvifeom">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'currentStage',
    label: 'Current Stage',
    renderCell: row => <Chip label={row.currentStage.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(139, 92, 246, 0.1)',
      color: '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2209" data-mp="a86sjw3g45vu" />
  }, {
    id: 'lifecycle',
    label: 'Lifecycle',
    renderCell: row => {
      const colors = {
        IN_PROGRESS: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        SUCCESS: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[row.lifecycle as keyof typeof colors];
      return <Chip label={row.lifecycle.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2210" data-mp="pqrgc1h0pi5r" />;
    }
  }, {
    id: 'transactionStatus',
    label: 'Transaction Status',
    renderCell: row => <Chip label={row.transactionStatus.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-2211" data-mp="2a6w948yhm0el" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      const colors = {
        IN_PROGRESS: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        COMPLETED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        REVERSED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[row.status as keyof typeof colors];
      return <Chip label={row.status.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2212" data-mp="w688q16a07be" />;
    }
  }, {
    id: 'rtgsStatus',
    label: 'RTGS Status',
    renderCell: row => {
      const colors = {
        SENT_TO_BANK: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        REVERSED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        },
        MANUALLY_SETTLED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        }
      };
      const style = colors[row.rtgsStatus];
      return <Chip label={row.rtgsStatus.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-2213" data-mp="hvqi525jokdm" />;
    }
  }, {
    id: 'sentToRTGSAt',
    label: 'Sent to RTGS',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap data-id="element-2214" data-mp="10oasb2dgs0s3">
          {new Date(row.sentToRTGSAt).toLocaleString()}
        </Typography>
  }];
  const sentToBankCount = transactions.filter(t => t.rtgsStatus === 'SENT_TO_BANK').length;
  return <>
      <Box sx={{
      mb: 2,
      display: 'flex',
      gap: 2,
      justifyContent: 'flex-end'
    }} data-id="element-2215" data-mp="2jix6dej8gi21">
        {selectedTransactions.size > 0 && <>
            <Button variant="outlined" startIcon={<Icon icon="lucide:x" width={16} data-id="element-2217" data-mp="2xif1ebn4grju" />} onClick={() => setSelectedTransactions(new Set())} data-id="element-2216" data-mp="2sjlnwj7u50jo">
              Clear ({selectedTransactions.size})
            </Button>
            <Button variant="contained" color="error" startIcon={<Icon icon="lucide:x-circle" width={16} data-id="element-2219" data-mp="3b91w7sprhxan" />} onClick={handleBulkCancelClick} sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          boxShadow: 'none'
        }} data-id="element-2218" data-mp="3776oa5dbslnw">
              Cancel Selected ({selectedTransactions.size})
            </Button>
            <Button variant="outlined" color="error" startIcon={<Icon icon="lucide:undo-2" width={16} data-id="element-2221" data-mp="1g4aml72s31o3" />} onClick={() => handleBulkAction('reverse')} data-id="element-2220" data-mp="1l2vrvkww0bje">
              Mark as Reversed
            </Button>
            <Button variant="contained" startIcon={<Icon icon="lucide:check-circle" width={16} data-id="element-2223" data-mp="23uy6kqpyxhr0" />} onClick={() => handleBulkAction('settle')} sx={{
          backgroundColor: '#10B981',
          '&:hover': {
            backgroundColor: '#059669'
          }
        }} data-id="element-2222" data-mp="1q306t6i5cy7i">
              Mark as Manually Settled
            </Button>
          </>}
      </Box>

      <DataTable title={`RTGS Transactions (${sentToBankCount} pending)`} data={transactions} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No RTGS transactions found" initialVisibleColumns={['select', 'id', 'mtoName', 'payoutType', 'amountFCY', 'currentStage', 'lifecycle', 'transactionStatus', 'status', 'rtgsStatus', 'sentToRTGSAt']} onRowClick={row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }} data-id="element-2224" data-mp="jf33e5pmt4nc" />

      {selectedTransaction && <TransactionDetailsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={{
      id: selectedTransaction.id,
      mtoName: selectedTransaction.mtoName,
      mtoRef: selectedTransaction.mtoRef,
      bankRef: selectedTransaction.bankRef,
      branchCode: selectedTransaction.branchCode,
      agentCode: selectedTransaction.agentCode,
      payoutType: selectedTransaction.payoutType,
      amountFCY: selectedTransaction.amountFCY,
      currencyFCY: selectedTransaction.currencyFCY,
      amountPKR: selectedTransaction.amountPKR,
      corridor: 'N/A',
      purposeCode: 'N/A',
      purposeText: 'N/A',
      currentState: 'RTGS_POST',
      lifecycleStatus: selectedTransaction.lifecycle as any,
      createdAt: selectedTransaction.createdAt,
      updatedAt: selectedTransaction.sentToRTGSAt,
      remitterName: selectedTransaction.remitterName,
      beneficiaryName: selectedTransaction.beneficiaryName,
      beneficiaryIBAN: selectedTransaction.beneficiaryIBAN,
      railRefs: {},
      stateHistory: [{
        stateCode: 'RTGS_POST',
        status: selectedTransaction.rtgsStatus === 'SENT_TO_BANK' ? 'PENDING' : selectedTransaction.rtgsStatus === 'MANUALLY_SETTLED' ? 'PASS' : 'FAIL',
        timestamp: selectedTransaction.sentToRTGSAt,
        primaryRef: `RTGS-${selectedTransaction.id}`,
        attempts: 1
      }]
    } as any} data-id="element-2225" data-mp="2cd07m6oc1tz5" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedCancelTransaction.id} cancellationAction="REVERSE_FINANCIAL_ACTION" data-id="element-2226" data-mp="3tb0itgnugzyh" />}

      <CancelTransactionDialog open={bulkCancelDialogOpen} onClose={() => setBulkCancelDialogOpen(false)} onConfirm={handleBulkCancelConfirm} transactionId={`${selectedTransactions.size} selected transactions`} cancellationAction="BULK_CANCEL" data-id="element-2227" data-mp="3alkiatc5t7i2" />

      {rtgsAction && <RTGSActionDialog open={rtgsDialogOpen} onClose={() => {
      setRtgsDialogOpen(false);
      setRtgsAction(null);
    }} onConfirm={handleRTGSConfirm} transaction={{
      id: `${selectedTransactions.size} transaction(s)`,
      mtoName: 'Multiple',
      amountFCY: 'Multiple',
      currencyFCY: ''
    }} action={rtgsAction} data-id="element-2228" data-mp="32le6p9vlprnj" />}
    </>;
};
export default RTGSTransactionsTable;